import React, { useState } from 'react';
import clsx from 'clsx';
import { useGlobal, globalizationPath } from '@rcg/ui-react';
import { Img } from 'react-image';
import { Chip } from '@material-ui/core';
import FlightIcon from '@material-ui/icons/Flight';
import Dictionary from '../../utils/dictionary';
import useStyles from './useStyles';
import FlightDetailsModal from '../ReviewCard/FlightDetailsModal';
import { ReactComponent as DefaultIcon } from '../../assets/icons/icon-missing-carrier.svg';
import { emptyFunction } from '../../utils/common';
import useDictionary from '../../hooks/useDictionary';
import { NAVIGATION_PATHS, FLIGHT_SEARCH_TYPE } from '../../utils/constants';
import FlexibilitySection from '../FareCard/components/FlexibilitySection';
import BaggageSection from '../FareCard/components/BaggageSection';
import AmenitiesSection from '../FareCard/components/AmenitiesSection';
import { isFlexibilityService, isBaggageService } from '../../utils/inflightServices';

const ReviewInfo = ({
  titlePreDate,
  flightIcon,
  date,
  startTime,
  endTime,
  numberOfStops,
  airlines,
  departureCity,
  arrivalCity,
  totalDuration,
  flightNumber,
  countStopsLabelBig,
  layoverDays,
  flightInfo,
  flightType,
  panelClass,
  isSummary,
  confirmationNumbers = null,
  isRefundable,
  firstSegment,
}) => {
  const {
    renderContent,
    data: { lang, market },
  } = useGlobal();
  const classes = useStyles();
  const getDictionaryValue = useDictionary(Dictionary);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const currentUrl = window.location.href;

  const isBrowsePath = currentUrl.includes(NAVIGATION_PATHS.browse);
  const isDepartureFlight = flightType.type === FLIGHT_SEARCH_TYPE.DEPARTURE;

  const isSummaryPage =
    currentUrl.includes(NAVIGATION_PATHS.reviewFlight) ||
    currentUrl.includes(NAVIGATION_PATHS.browseSummary) ||
    currentUrl.includes(NAVIGATION_PATHS.payment);

  const isConfirmationPage = currentUrl.includes(NAVIGATION_PATHS.confirmation);

  const {
    brandName,
    inflightServices = [],
    baggageInfo,
    isRefundableWithPenalty,
    currency,
  } = flightInfo?.cabin || {};

  const refundableLabel = (() => {
    if (isRefundableWithPenalty)
      return getDictionaryValue(Dictionary.airRefundableWithPenaltyLabel.key);
    if (isRefundable) return getDictionaryValue(Dictionary.airRefundableWithPenaltyLabel.key);
    return getDictionaryValue(Dictionary.airNotRefundableLabel.key);
  })();

  const valuePropChips = inflightServices.filter(
    (s) => s.isKeyFeature && (isFlexibilityService(s) || isBaggageService(s)),
  );

  const UPPERCASE_ACRONYMS = ['RCG'];

  const toTitleCase = (text) =>
    typeof text === 'string' && text === text.toUpperCase()
      ? text
          .toLowerCase()
          .split(' ')
          .map((word) =>
            UPPERCASE_ACRONYMS.includes(word.toUpperCase())
              ? word.toUpperCase()
              : word.charAt(0).toUpperCase() + word.slice(1),
          )
          .join(' ')
      : text;

  const showFareData = isSummaryPage || isConfirmationPage;
  const showFareSections = isSummaryPage && !isConfirmationPage;

  const editFlightUrl = isBrowsePath
    ? `${urlPath}${
        isDepartureFlight ? NAVIGATION_PATHS.browseDeparture : NAVIGATION_PATHS.browseReturn
      }`
    : `${urlPath}${
        isDepartureFlight ? NAVIGATION_PATHS.resultsDeparture : NAVIGATION_PATHS.resultsReturn
      }`;

  const isCodeshare =
    !!firstSegment && firstSegment.marketingAirline.code !== firstSegment.operatingAirline.code;

  const [openFlightDetailsModal, setOpenFlightDetailsModal] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);

  const handleOpenFlightDetailsModal = (info) => () => {
    setSelectedFlight(info);
    setOpenFlightDetailsModal(true);
  };
  const handleCloseFlightDetailsModal = () => {
    setSelectedFlight(null);
    setOpenFlightDetailsModal(false);
  };
  return (
    <>
      <div
        className={clsx(
          classes.cardFlightInfoBox,
          'cardFlightInfoBox',
          isSummary ? 'summary' : '',
        )}>
        <div
          className={clsx(
            classes.flightDateWrapper,
            isSummaryPage && classes.summaryLayout,
            isConfirmationPage && classes.confirmationLayout,
          )}>
          <div className={classes.iconAirlineAndDate}>
            <Img
              src={flightIcon}
              alt="flight-icon"
              className={classes.flightAirlineLogo}
              unloader={<DefaultIcon className={classes.flightAirlineLogo} />}
            />
            <div className={classes.airlineInfo}>
              <div className={classes.airlineNameRow}>
                {airlines && <span className={classes.airlineName}>{airlines}</span>}
                {flightNumber && (
                  // The separator belongs to the pair, not to the number: with no airline name
                  // (the BFF serves un-named flights when its catalogue lookup fails) this used to
                  // render a leading " | ".
                  <span className={classes.airlineFlightNum}>
                    {airlines ? ` | ${flightNumber}` : flightNumber}
                  </span>
                )}
              </div>
              {(isCodeshare || firstSegment?.airplane?.name) && (
                <span className={classes.flightInfoOperatedBy}>
                  {/* Same reason as above: the profile path never names the airplane, so the
                      separator was left dangling after the operator. */}
                  {isCodeshare &&
                    `${getDictionaryValue(Dictionary.airOperatorLabel.key)} ${
                      firstSegment.operatingAirline?.name ?? ''
                    }${firstSegment?.airplane?.name ? ' | ' : ''}`}
                  {firstSegment?.airplane?.name}
                </span>
              )}
            </div>
          </div>
          <div className="details-actions">
            <span
              data-testid="open-flight-details"
              className={!panelClass ? classes.flightDetails : classes.flightDetailsPanel}
              role="button"
              onKeyDown={emptyFunction}
              tabIndex="0"
              onClick={handleOpenFlightDetailsModal({
                ...flightInfo,
                flightType,
              })}>
              {getDictionaryValue(Dictionary.airFlightDetailsModalLabel.key)}
            </span>
          </div>

          <div className={clsx(classes.flightInfoInline, 'flightInfo')}>
            <div className={classes.timelineDeparture}>
              <p className={classes.timelineTime}>{startTime}</p>
              <p className={classes.timelineCity}>{departureCity}</p>
            </div>

            <div className={classes.timelineRoute}>
              <p className={classes.timelineRouteLabel}>{totalDuration}</p>
              <div className={classes.timelineRouteLine}>
                <span className={classes.timelineDot} />
                <span className={classes.timelineLineSeg} />
                <FlightIcon className={classes.timelineFlightIcon} />
                <span className={classes.timelineLineSeg} />
                <span className={classes.timelineDot} />
              </div>
              <p className={classes.timelineRouteLabel}>
                {numberOfStops} {countStopsLabelBig}
              </p>
            </div>

            <div className={classes.timelineArrival}>
              <p className={classes.timelineTime}>
                {endTime}
                {layoverDays && <sup className={classes.timelineDayOffset}>{layoverDays}</sup>}
              </p>
              <p className={classes.timelineCity}>{arrivalCity}</p>
            </div>
          </div>

          {confirmationNumbers && (
            <div className={classes.confirmationNumber}>
              {confirmationNumbers.length > 1 ? (
                <>
                  <span>
                    {getDictionaryValue(Dictionary.airFlightConfirmationNumbersLabel.key)}{' '}
                  </span>
                  <span className="singleLine">{confirmationNumbers[0]}</span>
                  <span>, {confirmationNumbers[1]}</span>
                </>
              ) : (
                <>
                  <span>
                    <span>
                      {getDictionaryValue(Dictionary.airFlightConfirmationNumberLabel.key)}{' '}
                    </span>
                    <span className="singleLine">{confirmationNumbers[0]}</span>
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <div className={classes.flightDateRow}>
          <span className={!panelClass ? classes.flightDate : classes.flightDatePanel}>
            {titlePreDate} — {date}
          </span>
          {isSummaryPage && !isConfirmationPage && (
            <a className={classes.flightDetails} href={editFlightUrl}>
              {getDictionaryValue(Dictionary.airFlightDetailsEditlLabel.key)}
            </a>
          )}
        </div>
        {showFareData && (
          <>
            {brandName && <p className={classes.brandName}>{toTitleCase(brandName)}</p>}
            <div className={classes.chipsRow}>
              <Chip label={refundableLabel} size="small" className={classes.propChip} />
              {valuePropChips.map((s) => (
                <Chip
                  key={s.key}
                  label={renderContent(s.key, s.value)}
                  size="small"
                  className={classes.propChip}
                />
              ))}
            </div>
          </>
        )}
        {showFareSections && (
          <div className={classes.fareDetails}>
            <div className={classes.fareDetailsLeft}>
              <FlexibilitySection inflightServices={inflightServices} />
              <BaggageSection baggageInfo={baggageInfo} currency={currency} />
            </div>
            <div className={classes.fareDetailsRight}>
              <AmenitiesSection inflightServices={inflightServices} />
            </div>
          </div>
        )}
      </div>
      <FlightDetailsModal
        openModal={openFlightDetailsModal}
        handleClose={handleCloseFlightDetailsModal}
        flightInfo={selectedFlight}
      />
    </>
  );
};

export default ReviewInfo;
